import _ from 'lodash';
import moment from 'moment';
import qs from 'qs';
import { useEffect, useState } from 'react';
import { safeParseInt } from '../../../../../../shared/common/utils';
import { asset } from '../../../../../../shared/frontend/services/assetResolverService';

export type TRWFPartner = {
  name: string;
  title: string;
  slug: string;
  logoWidth: number;
  logoHeight: number;
  logoLink: string;
  twitch?: string;
  douyu?: string;
  twitter?: string;
  facebook?: string;
  instagram?: string;
  youtube?: string;
  discord?: string;
  web?: string;
  customLink?: {
    url: string;
    title: string;
  };
  guildLogo?: string;
  embedImage?: string; // image to embed in streamm asthead
  backdropImage?: string; // backdrop to tile
  isStreamActive: boolean;
  streamSchedule: number[][];
  weight: number;
  streamType: 'twitch' | 'douyu';
  streamIds?: string[];
  isPartner?: boolean;
};

// https://raider.io/?utm_campaign=rwf_aberrus&utm_source=echo
export function getActivePartner(partners: TRWFPartner[], search: string) {
  const query = qs.parse(search, { ignoreQueryPrefix: true });
  if (query.utm_source && query.utm_campaign === 'rwf_aberrus') {
    return partners.find((p) => p.slug === query.utm_source);
  }

  if (window.location.hash) {
    return partners.find((p) => p.slug === window.location.hash.substring(1));
  }

  return null;
}

// the date when RWF is starting - so we can have relative date numbering
const RWF_EPOCH = moment.utc('2023-05-09T15:00:00');
function getCurrentEventDay(dateString?: string) {
  const date = moment.utc(dateString);
  const hours = moment.duration(date.diff(RWF_EPOCH)).asHours();
  return Math.max(0, Math.floor(hours / 24));
}

export function applyPartnerOverrides(partnerArray: any[], overridesDefinition: any) {
  // Can override schedule for a partner by setting an object in redis. For example:
  // hset features:dev:params rwf_aberrus_the_shadowed_crucible_partner_overrides '{"liquid": {"weight":10000,"streamSchedule":[[100, 2359], [300, 1400]]}'

  if (!overridesDefinition) {
    return;
  }

  try {
    const allOverrides = JSON.parse(overridesDefinition);
    for (const data of _.toPairs<any>(allOverrides)) {
      const [partnerSlug, overrides] = data as any;
      const partner = partnerArray.find((p: any) => p.slug === partnerSlug);
      if (!partner) {
        console.warn(`applyPartnerOverrides: got unknown partner ${partner}`);
        continue;
      }

      if (typeof overrides !== 'object') {
        console.warn(`applyPartnerOverrides: got invalid overrides object for partner ${partner}`, overrides);
        continue;
      }

      if (overrides.streamSchedule) {
        const newSchedule = overrides.streamSchedule;
        let allEntriesValid = true;
        for (const schedule of newSchedule) {
          if (schedule.length !== 2 && schedule.length !== 0) {
            allEntriesValid = false;
            break;
          }
        }
        if (allEntriesValid) {
          console.info(
            `applyPartnerOverrides: Updating schedule for ${partner.slug} from ${JSON.stringify(
              partner.streamSchedule
            )} to ${JSON.stringify(newSchedule)}`
          );
          partner.streamSchedule = _.cloneDeep(newSchedule);
        } else {
          console.warn(
            `applyPartnerOverrides: Not updating schedule for ${
              partner.slug
            } as one or more values are invalid in ${JSON.stringify(newSchedule)}`
          );
        }
      }

      const FIELD_MAPPERS = [
        { field: 'weight', valueMapper: safeParseInt },
        { field: 'isStreamActive', valueMapper: (v: any) => !!v }
      ];

      for (const { field, valueMapper } of FIELD_MAPPERS) {
        if (typeof overrides[field] !== 'undefined') {
          console.info(
            `applyPartnerOverrides: Updating field ${field} for ${partner.slug} from ${partner[field]} to ${overrides[field]}`
          );
          partner[field] = valueMapper(overrides[field]);
        } else {
          console.info(`applyPartnerOverrides: nothing found for field ${field} in ${JSON.stringify(overrides)}`);
        }
      }
    }
  } catch (err) {
    // some error in the JSON so just ignore the update
    console.warn('applyPartnerOverrides: Unable to load overrides', err);
  }
}

export function chooseBestPartner(partners: TRWFPartner[], militaryTime: number, randomUnitValue: number) {
  let activePartners = partners.filter((partner: any) => {
    if (!partner.isStreamActive || !partner.streamSchedule.length) {
      return false;
    }

    let startTime;
    let endTime;

    const day = getCurrentEventDay();
    if (day >= partner.streamSchedule.length) {
      // just repeat schedule from last day
      [startTime, endTime] = partner.streamSchedule[partner.streamSchedule.length - 1];
    } else {
      // has an entry for this day
      [startTime, endTime] = partner.streamSchedule[day];
    }

    if (typeof startTime === 'undefined' || typeof endTime === 'undefined') {
      return false;
    }

    if (startTime < endTime) {
      // starts earlier in day and ends later in day
      return militaryTime >= startTime && militaryTime <= endTime;
    } else {
      // starts at end of day and ends at start of next day
      return militaryTime >= startTime || militaryTime <= endTime;
    }
  });

  if (activePartners.length === 0) {
    // just choose based on streams marked active and their weights as fallback if we found nothing with time checks
    activePartners = partners.filter((partner) => partner.isStreamActive);
  }

  const activeWeight = _.sum(activePartners.map((partner: any) => partner.weight));

  // for (const partner of activePartners) {
  //   console.log('chooseBestPartner', partner.name, (100 * (partner.weight / activeWeight)).toFixed(1), '%');
  // }

  const val = randomUnitValue * activeWeight;
  let cnt = 0;

  for (const partner of activePartners) {
    cnt += partner.weight;
    if (val <= cnt) {
      return partner;
    }
  }

  return null;
}

export const CoverageTheme = (props: any) => {
  const [activePartner, setActivePartner] = useState('');

  useEffect(() => {
    const partner = getActivePartner(props.partners, props.location.search);
    if (partner) {
      // show a background if coming in with proper params
      setActivePartner(partner.slug);

      // set the page backdrop
      const elem = document.getElementById('application');
      if (elem) {
        elem.style.background = `url(${asset(`/images/rwf/${props.raidSlug}/${partner.slug}/backdrop.png`)}) repeat`;
      }
    }

    return () => {
      // clear the background when we leave the page
      if (activePartner) {
        const elem = document.getElementById('application');
        if (elem) {
          elem.style.background = '';
        }
      }
    };
  }, []);

  return null;
};

export const withRWFPartners = (raidSlug: string, partners: any[], CoverageComponent: any) => {
  return (props: any) => {
    return <CoverageComponent {...props} raidSlug={raidSlug} partners={partners} />;
  };
};
